// Import helpers needed for calculations
import { extractTaxRate, groupTaxesByRate } from './taxHelpers';
import { formatEventDate } from './dateHelpers';

/**
 * Process API items for display in the checkout
 * @param {Object} orderData - Order data from API
 * @returns {Array} Processed items for display
 */
export const processApiItems = (orderData) => {
	if (!orderData?.item?.article || !Array.isArray(orderData.item.article)) return [];
	
	return orderData.item.article.map(article => {
		const instanceData = article.instance || {};
		const articleEvent = instanceData.event || {};
		const articleCost = instanceData.cost || {};
		
		// Convert count from string to number for display purposes
		const count = parseInt(article.count || 1, 10);
		
		// Extract tax information
		const taxRate = extractTaxRate(article);
		
		// Calculate price and total amounts
		const price = Number(articleCost.sale_price || 0);
		const grossAmount = price * count;
		const netAmount = grossAmount / (1 + taxRate / 100);
		const taxAmount = grossAmount - netAmount;
		
		return {
			id: article.id || instanceData.id,
			product_name: instanceData.title || "Article",
			description: articleEvent.title
				? `${articleEvent.title} (${formatEventDate(articleEvent.date, articleEvent.end_date)})`
				: instanceData.desc_long || instanceData.desc || "",
			price: price,
			quantity: count,
			event_date: articleEvent.date,
			event_end_date: articleEvent.end_date,
			originalArticle: article,
			// Store event details directly with their correct paths
			originalEvent: articleEvent,
			taxRate: taxRate,
			taxAmount: taxAmount,
			netAmount: netAmount,
			grossAmount: grossAmount
		};
	});
};

/**
 * Calculates the order totals from items
 * @param {Array} items - The order items
 * @param {Object} discountInfo - Optional discount information
 * @returns {Object} Order totals
 */
export const calculateOrderTotals = (items, discountInfo = null) => {
	// Group taxes by rate
	const taxGroups = groupTaxesByRate(items);
	
	// Calculate subtotal (before discount)
	const subtotal = items.reduce((sum, item) => {
		return sum + (item.price * item.quantity);
	}, 0);
	
	// Calculate discount if applicable
	const discount = discountInfo ? Number(discountInfo.value || 0) : 0;
	
	// Calculate discounted subtotal
	const discountedSubtotal = Math.max(0, subtotal - discount);
	
	// Calculate total tax
	const totalTax = Object.values(taxGroups).reduce((sum, taxAmount) => sum + taxAmount, 0);
	
	// Calculate final total
	const total = discountedSubtotal;
	
	return {
		subtotal,
		discount,
		discountedSubtotal,
		tax: totalTax,
		total,
		taxGroups
	};
};
